import React from 'react';

import { useIntl } from 'react-intl-next';
import { useLazyLoadQuery } from 'react-relay';
import { useRouterActions } from 'react-resource-router';
import { graphql } from 'relay-runtime';

import Button from '@atlaskit/button';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import PageIcon from '@atlaskit/icon/core/page';
import { HelpPageAri } from '@atlassian/ari/help';
import { useHubContext } from '@atlassian/company-hub-common';

import type { moreDropdownPagesQuery } from './__generated__/moreDropdownPagesQuery.graphql';
import messages from './messages';

export const MoreDropdown = ({ textColor }: { textColor?: string }): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { push } = useRouterActions();
	const {
		cloudId,
		env: { helpCenterAri },
	} = useHubContext();

	const data = useLazyLoadQuery<moreDropdownPagesQuery>(
		graphql`
			query moreDropdownPagesQuery($cloudId: ID!, $hubAri: ID!) {
				helpCenter(cloudId: $cloudId) {
					helpCenterById(helpCenterAri: $hubAri) @optIn(to: "HelpCenterAggBeta") {
						__typename
						... on HelpCenter {
							customPages: pages {
								id
								name {
									default
								}
							}
						}
					}
				}
			}
		`,
		{ cloudId, hubAri: helpCenterAri },
	);

	const customPages =
		data?.helpCenter?.helpCenterById?.__typename === 'HelpCenter'
			? data?.helpCenter?.helpCenterById?.customPages
			: [];
	const visiblePages = customPages?.filter((p) => p?.name?.default);
	if (!visiblePages || visiblePages.length === 0) {
		return null;
	}

	return (
		<DropdownMenu<HTMLButtonElement>
			trigger={({ triggerRef, ...props }) => (
				<Button
					{...props}
					appearance="subtle"
					style={{ color: props.isSelected ? undefined : textColor }}
					iconAfter={<ChevronDownIcon label={formatMessage(messages.moreDownIcon)} />}
					ref={triggerRef}
				>
					{formatMessage(messages.more)}
				</Button>
			)}
			shouldRenderToParent
		>
			<DropdownItemGroup>
				{visiblePages.map((page) => {
					const pageId = page?.id ? HelpPageAri.parse(page.id).pageId : '';
					return (
						<DropdownItem
							key={pageId}
							testId={`nav-custom-page-${pageId}`}
							elemBefore={<PageIcon label={formatMessage(messages.pageIconLabel)} />}
							onClick={() => {
								push(`/page/${pageId}`);
							}}
						>
							{page?.name?.default}
						</DropdownItem>
					);
				})}
			</DropdownItemGroup>
		</DropdownMenu>
	);
};
